import { create } from 'zustand'

import { z } from 'zod'

import { QrResult } from '@ca/common/types'
import { useCallback } from 'react'
import { AddressSchema, BirthdateSchema, InvoiceSchema, LoginUtil } from '@ca/common'
import { contactModalSchema } from '@user/customer/contactSchemas'

export { contactModalSchema }
import { useStaff } from './useSession'
import { router } from 'expo-router'

export const contactStepSchema = z.object({
  mobile: z.string().optional(),
  email: z.string().optional(),
})

export const landlineSchema = z.object({
  phone: z.string(),
})

export const contactStepValidationSchema = contactStepSchema.superRefine(
  (val, ctx) => {
    if (!val.mobile && !val.email) {
      ctx.addIssue({
        path: ['email'],
        code: 'custom',
        message: 'required',
      })
      ctx.addIssue({
        path: ['mobile'],
        code: 'custom',
        message: 'required',
      })
      return
    }
    if (val.mobile && !LoginUtil.isPhone(val.mobile)) {
      ctx.addIssue({
        path: ['mobile'],
        code: 'custom',
        message: 'mobile.invalid',
      })
    }
    if (val.email && !LoginUtil.isEmail(val.email)) {
      ctx.addIssue({
        path: ['email'],
        code: 'custom',
        message: 'email.invalid',
      })
    }
  }
)

export const birthdayStepSchema = z.object({
  birthday: BirthdateSchema,
})

export const personalInfoStepSchema = z.object({
  first_name: z.string().min(2, 'minLength.2'),
  last_name: z.string().min(2, 'minLength.2'),
  // title: z.string(),
})

export type PersonalInfoValues = z.infer<typeof personalInfoStepSchema>

const formSchema = z.object({
  ...personalInfoStepSchema.shape,
  ...contactStepSchema.shape,
  ...birthdayStepSchema.shape,
  phone: landlineSchema.shape.phone.optional(),
  address: AddressSchema,
  invoice: InvoiceSchema.optional(),
  policies: z.record(z.string(), z.boolean()).optional(),
})

type FormSchema = z.infer<typeof formSchema>

type IntroMode = 'none' | 'login' | 'createAccount' | 'staff'

type IntroStoreState = {
  createData: Partial<FormSchema>
  updateData: (d: Partial<FormSchema>) => void
  clearRedirect: () => void
  mode: IntroMode
  redirect: string | null
  qr: QrResult | null
  goBack: boolean
  prevScreen: string | undefined
  toggleMode: (
    mode: IntroMode,
    options?: {
      qr?: QrResult
      redirect?: string
      goBack?: boolean
    }
  ) => void
  setPrevScreen: (prevScreen: string | undefined) => void
  reset: () => void
}

export const useIntroStore = create<IntroStoreState>((set) => ({
  mode: 'none' as const,
  redirect: null,
  qr: null,
  createData: {},
  goBack: false,
  prevScreen: undefined,
  updateData: (d: Partial<FormSchema>) => {
    set((state) => ({
      createData: {
        ...state.createData,
        ...d,
      },
    }))
  },
  reset: () => {
    set({
      mode: 'none',
      createData: {},
      qr: null,
      goBack: false,
      prevScreen: undefined,
    })
  },
  setPrevScreen: (prevScreen) => set({ prevScreen }),
  clearRedirect: () => set({ redirect: null }),
  toggleMode: (mode, options) => {
    set({
      mode,
      createData: {},
      redirect: options?.redirect || null,
      qr: options?.qr || null,
      goBack: options?.goBack ?? false,
    })
  },
}))

export const useToggleStaffModal = () => {
  const { toggleMode } = useIntroStore()
  return useCallback(() => toggleMode('staff'), [toggleMode])
}

export const useStaffNavigate = () => {
  const { toggleMode } = useIntroStore()
  const isStaff = useStaff()

  const navigate = useCallback(
    (redirect: string) => {
      if (isStaff) {
        router.navigate(redirect)
      } else {
        toggleMode('staff', { redirect })
      }
    },
    [toggleMode, isStaff]
  )

  return navigate
}

export default useIntroStore
